/**
 * Brand List Tab Navigation with URL Parameters
 * Handles tab switching for woman/man/all collections
 * with URL query parameter support
 */

class BrandListTabs {
  constructor(container) {
    this.container = container;
    this.tabs = container.querySelectorAll(".tab");
    this.panels = container.querySelectorAll(".panel");
    this.letterFilters = container.querySelectorAll(".letters-container--item");
    this.searchInputs = [];
    this.currentTab = null;

    this.init();
  }

  init() {
    // Get collection from URL parameter or default to first tab
    const urlParams = new URLSearchParams(window.location.search);
    const collectionParam = urlParams.get("collection");

    // Set initial active tab
    if (collectionParam) {
      this.activateTabByCollection(collectionParam);
    } else {
      this.activateTab(0);
    }

    // Bind tab click events
    this.tabs.forEach((tab, index) => {
      tab.addEventListener("click", (e) => {
        e.preventDefault();
        const collection = tab.getAttribute("data-collection");
        this.switchTab(index, collection);
      });

      // Keyboard navigation
      tab.addEventListener("keydown", (e) => {
        this.handleKeyboard(e, index);
      });
    });

    // Bind letter filter clicks
    this.letterFilters.forEach((letter) => {
      letter.addEventListener("click", (e) => {
        e.preventDefault();
        this.filterByLetter(letter);
      });
    });

    // Initialize letter filters for the active panel
    this.initializeLetterFilters();
    this.initSearchInputs();
  }

  handleKeyboard(e, currentIndex) {
    let newIndex = currentIndex;

    switch (e.key) {
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : this.tabs.length - 1;
        break;
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        newIndex = currentIndex < this.tabs.length - 1 ? currentIndex + 1 : 0;
        break;
      case "Home":
        e.preventDefault();
        newIndex = 0;
        break;
      case "End":
        e.preventDefault();
        newIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    const collection = this.tabs[newIndex].getAttribute("data-collection");
    this.switchTab(newIndex, collection);
  }

  activateTabByCollection(collection) {
    const tabIndex = Array.from(this.tabs).findIndex(
      (tab) => tab.getAttribute("data-collection") === collection,
    );

    if (tabIndex !== -1) {
      this.activateTab(tabIndex);
    } else {
      this.activateTab(0);
    }
  }

  activateTab(index) {
    // Remove active class from all tabs and panels
    this.tabs.forEach((tab, i) => {
      tab.classList.remove("active");
      tab.setAttribute("aria-selected", "false");
      tab.setAttribute("tabindex", "-1");
    });

    this.panels.forEach((panel, i) => {
      panel.classList.remove("active");
      if (i !== index) {
        panel.setAttribute("hidden", "");
      } else {
        panel.removeAttribute("hidden");
      }
    });

    // Add active class to selected tab and panel
    if (this.tabs[index]) {
      this.tabs[index].classList.add("active");
      this.tabs[index].setAttribute("aria-selected", "true");
      this.tabs[index].setAttribute("tabindex", "0");
      this.tabs[index].focus();
    }

    if (this.panels[index]) {
      const activePanel = this.panels[index];
      activePanel.classList.add("active");
      this.currentTab = index;

      // Animate brands opacity
      this.resetSearchInput(activePanel);
      this.animateBrands(activePanel);

      // Initialize letter filters for this panel
      this.initializeLetterFilters();
    }
  }

  switchTab(index, collection) {
    this.activateTab(index);

    // Update URL with query parameter
    this.updateURL(collection);
  }

  updateURL(collection) {
    const url = new URL(window.location);
    url.searchParams.set("collection", collection);
    window.history.pushState({}, "", url);
  }

  initializeLetterFilters() {
    const activePanel = this.panels[this.currentTab];
    if (!activePanel) return;

    const letters = activePanel.querySelectorAll(".letters-container--item");
    const brandItems = activePanel.querySelectorAll(".brand-name");

    // Find which letters have brands
    const availableLetters = new Set();
    brandItems.forEach((brand) => {
      const letter = brand.getAttribute("data-letter");
      if (letter) {
        availableLetters.add(letter);
      }
      brand.classList.remove("hide");
      brand.style.display = "";
    });

    // Update letter filter states
    letters.forEach((letter) => {
      const letterValue = letter.getAttribute("data-letter");
      if (availableLetters.has(letterValue)) {
        letter.classList.remove("hide");
      } else {
        letter.classList.add("hide");
      }
      letter.classList.remove("active");
    });

    this.setDefaultActiveLetter(activePanel);
    this.updateEmptyState(activePanel, false);
  }

  initSearchInputs() {
    this.searchInputs = this.container.querySelectorAll(
      ".brands-search__input",
    );

    this.searchInputs.forEach((input) => {
      input.addEventListener("input", (event) => {
        const panel = event.target.closest(".panel");
        if (!panel) return;

        const query = event.target.value.trim().toLowerCase();
        this.applySearchFilter(panel, query);

        if (!query && panel === this.panels[this.currentTab]) {
          this.initializeLetterFilters();
        }
      });
    });
  }

  resetSearchInput(panel) {
    if (!panel) return;
    const searchInput = panel.querySelector(".brands-search__input");
    if (searchInput) {
      searchInput.value = "";
    }
    this.updateEmptyState(panel, false);
  }

  applySearchFilter(panel, query) {
    const brandItems = panel.querySelectorAll(".brand-name");
    const letters = panel.querySelectorAll(".letters-container--item");
    const searchTerm = (query || "").toLowerCase();
    const hasQuery = searchTerm.length > 0;
    const visibleLetters = new Set();
    let matchesFound = !hasQuery;

    brandItems.forEach((brand) => {
      const brandText = brand.textContent.toLowerCase();
      const matches = !hasQuery || brandText.includes(searchTerm);
      if (matches) {
        brand.classList.remove("hide");
        brand.style.display = "";
        const letter = brand.getAttribute("data-letter");
        if (letter) {
          visibleLetters.add(letter);
        }
        if (hasQuery) {
          matchesFound = true;
        }
      } else {
        brand.classList.add("hide");
        brand.style.display = "none";
      }
    });

    letters.forEach((letter) => {
      const value = letter.getAttribute("data-letter");
      if (visibleLetters.has(value)) {
        letter.classList.remove("hide");
      } else {
        letter.classList.add("hide");
      }
    });

    if (hasQuery) {
      this.clearActiveLetters(panel);
    }

    this.updateEmptyState(panel, hasQuery && !matchesFound);
  }

  updateEmptyState(panel, isEmpty) {
    const message = panel.querySelector(".brands-search__empty");
    if (!message) return;
    message.hidden = !isEmpty;
  }

  clearActiveLetters(panel) {
    const letters = panel.querySelectorAll(".letters-container--item");
    letters.forEach((letter) => letter.classList.remove("active"));
  }

  setDefaultActiveLetter(panel) {
    const letters = panel.querySelectorAll(".letters-container--item");
    if (!letters.length) return;

    this.clearActiveLetters(panel);

    const firstVisible = Array.from(letters).find(
      (letter) => !letter.classList.contains("hide"),
    );
    if (firstVisible) {
      firstVisible.classList.add("active");
    }
  }

  filterByLetter(letterElement, scrollToTop = true) {
    const activePanel = this.panels[this.currentTab];
    if (!activePanel) return;

    const searchInput = activePanel.querySelector(".brands-search__input");
    if (searchInput && searchInput.value.trim().length > 0) {
      searchInput.value = "";
      this.updateEmptyState(activePanel, false);
      this.initializeLetterFilters();
    }

    const selectedLetter = letterElement.getAttribute("data-letter");
    const letters = activePanel.querySelectorAll(".letters-container--item");
    const brandItems = activePanel.querySelectorAll(".brand-name");

    // Update letter filter active state
    letters.forEach((letter) => letter.classList.remove("active"));
    letterElement.classList.add("active");

    // Show all brands for the selected gender (don't filter by letter)
    brandItems.forEach((brand) => {
      brand.classList.remove("hide");
      brand.style.display = "";
    });

    // Scroll to the selected letter section if scrollToTop is true
    if (scrollToTop) {
      const firstBrandWithLetter = activePanel.querySelector(
        `.brand-name[data-heading="${selectedLetter}"]`,
      );
      if (firstBrandWithLetter) {
        firstBrandWithLetter.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } else {
        const brandsContainer = activePanel.querySelector(
          ".brands-banner__list__group__letters",
        );
        if (brandsContainer) {
          brandsContainer.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }
    }
  }

  animateBrands(panel) {
    const brandsContainer = panel.querySelector(
      ".brands-banner__list__group__letters",
    );
    if (!brandsContainer) return;

    // Fade in animation
    brandsContainer.style.opacity = "0";

    setTimeout(() => {
      brandsContainer.style.transition = "opacity 0.3s ease-in-out";
      brandsContainer.style.opacity = "1";
    }, 50);
  }
}

// Initialize on DOM ready
document.addEventListener("DOMContentLoaded", () => {
  const brandListContainer = document.querySelector(
    ".brands-list__section .tabs__container",
  );

  if (brandListContainer) {
    new BrandListTabs(brandListContainer);
  }
});

// Handle browser back/forward navigation
window.addEventListener("popstate", () => {
  const brandListContainer = document.querySelector(
    ".brands-list__section .tabs__container",
  );

  if (brandListContainer) {
    const urlParams = new URLSearchParams(window.location.search);
    const collectionParam = urlParams.get("collection");

    const tabs = brandListContainer.querySelectorAll(".tab");
    const tabIndex = Array.from(tabs).findIndex(
      (tab) => tab.getAttribute("data-collection") === collectionParam,
    );

    if (tabIndex !== -1) {
      const instance = new BrandListTabs(brandListContainer);
      instance.activateTab(tabIndex);
    }
  }
});
